"use client";

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";

type DemoLink = (typeof DEMO_LINKS)[number];

type LandingDemoMenuProps = {
  align?: "start" | "end";
  className?: string;
  menuClassName?: string;
  variant?: "default" | "inverse";
  label?: string;
  leadingIcon?: ReactNode;
};

const DEMO_LINKS = [
  {
    to: "/paintbooth-demo",
    title: "Paint Booth Demo",
    description: "Live 3D livery preview demo",
  },
  {
    to: "/paintbooth-studio-demo",
    title: "Paint Booth Studio Demo",
    description: "Layer-based studio demo",
  },
];

export function LandingDemoMenu({
  align = "start",
  className,
  menuClassName,
  variant = "default",
  label = "Try Demo",
  leadingIcon,
}: LandingDemoMenuProps) {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<{ left: number; top: number } | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  // Demos are ungated now, so every visitor navigates straight in.
  const handleDemoSelect = (item: DemoLink) => {
    setOpen(false);
    navigate(item.to);
  };

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!rootRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        setOpen(false);
      }
    };

    const handleViewportChange = () => {
      const rect = rootRef.current?.getBoundingClientRect();
      if (!rect) return;
      const menuWidth = 288;
      const left = align === "end" ? rect.right - menuWidth : rect.left;
      setMenuStyle({
        left: Math.max(16, Math.min(left, window.innerWidth - menuWidth - 16)),
        top: rect.bottom + 12,
      });
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    handleViewportChange();
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [align, open]);

  useLayoutEffect(() => {
    if (!open) return;
    const rect = rootRef.current?.getBoundingClientRect();
    if (!rect) return;
    const menuWidth = 288;
    const left = align === "end" ? rect.right - menuWidth : rect.left;
    setMenuStyle({
      left: Math.max(16, Math.min(left, window.innerWidth - menuWidth - 16)),
      top: rect.bottom + 12,
    });
  }, [align, open]);

  return (
    <div ref={rootRef} className="relative z-[80]">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className={cn("inline-flex items-center justify-center whitespace-nowrap", className)}
      >
        {leadingIcon}
        {label}
        <ChevronDown className={cn("ml-2 h-4 w-4 transition", open ? "rotate-180" : "")} />
      </button>

      {open && menuStyle
        ? createPortal(
            <div
              ref={menuRef}
              className={cn(
                "fixed z-[9999] min-w-[18rem] overflow-hidden rounded-2xl p-2 shadow-[0_22px_70px_rgba(0,0,0,0.28)] backdrop-blur-xl",
                variant === "default" && "border border-[hsl(var(--lb-line))] bg-[hsl(var(--lb-panel))]",
                variant === "inverse" && "bg-[#17120f]",
                menuClassName,
              )}
              style={menuStyle}
            >
              {DEMO_LINKS.map((item) => (
                <button
                  key={item.to}
                  type="button"
                  onClick={() => handleDemoSelect(item)}
                  className={cn(
                    "block w-full rounded-xl px-4 py-3 text-left transition",
                    variant === "default" && "hover:bg-[hsl(var(--lb-bg-soft))]",
                    variant === "inverse" && "hover:bg-[rgba(255,255,255,0.08)]",
                  )}
                >
                  <div
                    className={cn(
                      "text-sm font-semibold",
                      variant === "default" && "text-[hsl(var(--lb-ink))]",
                      variant === "inverse" && "text-white",
                    )}
                  >
                    {item.title}
                  </div>
                  <div
                    className={cn(
                      "mt-0.5 text-xs",
                      variant === "default" && "text-[hsl(var(--lb-muted))]",
                      variant === "inverse" && "text-white/70",
                    )}
                  >
                    {item.description}
                  </div>
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
